import type { TOC } from '@ember/component/template-only';
import Text from 'tio-ui/components/text';
import { PhCheckCircle, PhHourglassHigh } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * BenefitOpportunity type representing an employer benefit
 */
export interface BenefitOpportunity {
  id: string;
  title: string;
  description: string;
  value: string;
  deadline?: string;
  enrolled: boolean;
  eligible: boolean;
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
}

/**
 * BenefitCard - Displays an employer benefit opportunity
 *
 * Shows benefit enrollment status, value, and optional deadline.
 * Enrolled benefits are highlighted with a ring indicator.
 *
 * @example
 * ```gts
 * <BenefitCard @benefit={{this.benefit}} />
 * ```
 */
interface BenefitCardSignature {
  Args: {
    /** The benefit opportunity data to display */
    benefit: BenefitOpportunity;
  };
  Element: HTMLDivElement;
}

const BenefitCard: TOC<BenefitCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div
    class="tio-benefit-card {{if @benefit.enrolled 'tio-benefit-card--enrolled'}}"
    role="article"
    aria-label="{{@benefit.title}}: {{@benefit.description}}"
    ...attributes
  >
    <div class="tio-benefit-card__icon {{if @benefit.enrolled 'tio-benefit-card__icon--enrolled'}}">
      {{#let @benefit.icon as |BenefitIcon|}}
        <BenefitIcon @size={{22}} />
      {{/let}}
    </div>
    <div class="tio-benefit-card__body">
      <div class="tio-benefit-card__title-row">
        <span class="tio-benefit-card__title">{{@benefit.title}}</span>
        {{#if @benefit.enrolled}}
          <PhCheckCircle @size={{16}} class="tio-fg-success" aria-label="Enrolled" />
        {{/if}}
      </div>
      <Text @variant="muted" @size="sm" class="mb-2">{{@benefit.description}}</Text>
      <div class="tio-benefit-card__footer">
        <span class="tio-benefit-card__value">{{@benefit.value}}</span>
        {{#if @benefit.deadline}}
          <span class="tio-benefit-card__deadline">
            <PhHourglassHigh @size={{12}} aria-hidden="true" />
            <span>Expires {{@benefit.deadline}}</span>
          </span>
        {{/if}}
      </div>
    </div>
  </div>
</template>;

export default BenefitCard;
